import { ExtractInsightsDocument, FindInsightState } from '@cycle-app/graphql-codegen';
import { ReactNode } from 'react';

import { useDocPanelContext } from 'src/contexts/docPanelContext';
import { useProduct, useSafeMutation } from 'src/hooks';
import { useDocState } from 'src/hooks/api/useDocState';
import { useCheckMonthlyAiQueries } from 'src/hooks/billing/useCheckMonthlyAiQueries';
import { setLimitationsModal, getPermission } from 'src/reactives';
import client from 'src/services/apollo/client';
import { requestInsightCreate } from 'src/utils/requestInsightCreate.utils';

export const ExtractInsights = ({ button }: {
  button: (props: { onClick: VoidFunction; isLoading: boolean; disabled: boolean }) => ReactNode;
}) => {
  const docId = useDocPanelContext(ctx => ctx.docId);

  const [extract] = useSafeMutation(ExtractInsightsDocument, {
    onCompleted: () => {
      client.cache.modify({
        id: client.cache.identify({ id: docId }),
        fields: {
          findInsightState: () => FindInsightState.Unprocessed,
          autopilotState: () => null,
        },
      });
    },
  });

  const {
    autopilotState, findInsightState,
  } = useDocState(docId);
  const { product } = useProduct();
  const {
    maxAiQueriesReached,
    openUnlimitedAiPrompt,
  } = useCheckMonthlyAiQueries(product?.nbAiQueries);

  const onClick = async () => {
    const { canUseAi } = getPermission();

    if (!canUseAi) {
      setLimitationsModal({ action: 'USE_AI' });
      return;
    }

    if (maxAiQueriesReached) {
      openUnlimitedAiPrompt();
      return;
    }

    const canCreateInsight = requestInsightCreate();
    if (!canCreateInsight) return;

    await extract({
      variables: { docId },
    });
  };

  const isLoading = !!findInsightState;
  const disabled = !!autopilotState || !!findInsightState;

  return (
    <>
      {button({
        onClick,
        isLoading,
        disabled,
      })}
    </>
  );
};
